import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { User, Lock, Loader2, Church, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import afterLogin, { extractRedirect } from "@/lib/afterLogin";

const MemberLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const loginMutation = useMutation({
    mutationFn: (data) => api.post("/member/login", data),
    onSuccess: async (response) => {
      localStorage.setItem("member_token", response.data.access_token);
      localStorage.setItem("member_data", JSON.stringify(response.data.member));
      const member = response.data.member || {};
      // Promoted to an office that runs an assembly — the session becomes an
      // admin one so the church workspace opens with their new permissions.
      if (member.is_church_admin) {
        useAuthStore.getState().setAuth(response.data.access_token, member, 'admin',
                                        null, member.permissions || []);
      }
      toast.success(member.office_label ? `Welcome back, ${member.office_label}` : "Welcome back!");
      await afterLogin({
        navigate, userType: member.is_church_admin ? "admin" : "member", tenantId: null,
        redirect: extractRedirect(), token: response.data.access_token,
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Login failed");
    },
  });

  const resetMutation = useMutation({
    mutationFn: (email) => api.post("/member/request-password-reset", { email }),
    onSuccess: () => {
      toast.success("If this email is registered, you will receive reset instructions.");
      setShowForgotPassword(false);
      setResetEmail("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    loginMutation.mutate(formData);
  };

  const handleResetSubmit = (e) => {
    e.preventDefault();
    resetMutation.mutate(resetEmail);
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
            <button
              onClick={() => setShowForgotPassword(false)}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </button>
            
            <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
            <p className="text-white/70 mb-6">Enter your email to receive reset instructions</p>
            
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm mb-2">Email Address</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={resetMutation.isPending}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {resetMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-4">
            <Church className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Member Portal</h1>
          <p className="text-white/70">Access your contributions and messages</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white/80 text-sm mb-2">Email Address</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-emerald-400 hover:text-emerald-300 text-sm"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/20 text-center">
            <p className="text-white/60 text-sm">
              Not a member yet?{" "}
              <Link to="/member/register" className="text-emerald-400 hover:text-emerald-300">
                Request to join
              </Link>
            </p>
          </div>
        </div>

        {/* Back to main site */}
        <div className="text-center mt-6">
          <Link to="/" className="text-white/60 hover:text-white text-sm">
            ← Back to main site
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MemberLoginPage;
